import React, { useReducer, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext } from 'react-beautiful-dnd';

import reducer from '../../../../store/componentReducer';
import { init, setValue } from '../../../../store/actions';
import { applyDrag, updateAt, removeAt } from './MainNavComponents/navEditorState';
import NavItemsBar from './MainNavComponents/NavItemsBar';
import NavCityTabs from './MainNavComponents/NavCityTabs';
import NavCityHeader from './MainNavComponents/NavCityHeader';
import NavColumnsBoard from './MainNavComponents/NavColumnsBoard';
import NavLinksArea from './MainNavComponents/NavLinksArea';

const initialState = {
  data: {
    type: 'MainNavMenu',
    name: 'mainNavMenu',
    items: [],
    secondaryLinks: [],
  },
};

// Mirror editor for the redesigned main menu: the admin takes the shape of the
// rendered menu (items row -> city tabs -> city header -> columns side by
// side). Texts display their EN value; clicking one opens a stacked editor
// with all four languages (EN required, the rest fall back to EN).
// Own DragDropContext nested inside the page builder's — same pattern as
// TicketsTabs.
const MainNavMenu = ({ components, handlerSetComponents, id }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [selItem, setSelItem] = useState(0);
  const [selCity, setSelCity] = useState(0);
  const { data } = state;

  const componentIndex = components.findIndex(obj => obj.id === id);
  const { component } = components[componentIndex];

  useEffect(() => {
    if (component) init(dispatch)(component);
  }, []);

  // The desktop header links (headerSubmenu inside the menuContainer sibling)
  // must always match the drawer's secondary links; this editor is the single
  // source of truth, so mirror the list into that sibling on every change.
  const syncDesktopSubmenu = () => {
    // First render happens before init() loads the real component — don't
    // mirror the empty initial state over the sibling's params.
    if ((data.items || []).length === 0 && (data.secondaryLinks || []).length === 0) return;
    const slug = t => ((t && t.en) || '').toLowerCase().trim().replace(/\s+/g, '-');
    const mcIndex = components.findIndex(row => row.component && row.component.type === 'HeaderRedesignTopHeaderMenuContainer');
    if (mcIndex === -1) return;
    const mc = JSON.parse(JSON.stringify(components[mcIndex].component));
    const submenu = (mc.components || []).find(c => c && c.type === 'HeaderRedesignTopHeaderSubmenu');
    if (!submenu) return;
    const params = (data.secondaryLinks || []).map(l => ({ text: l.text || {}, link: l.url || '', key: slug(l.text) }));
    let container = (submenu.components || []).find(c => c && c.type === 'HeaderRedesignTopHeaderSubmenuItemsContainer');
    if (!container) {
      container = {
        type: 'HeaderRedesignTopHeaderSubmenuItemsContainer', name: 'subMenu', mainParent: 'subMenu', params: [],
      };
      submenu.components = (submenu.components || []).concat([container]);
    }
    if (JSON.stringify(container.params) === JSON.stringify(params)) return;
    container.params = params;
    handlerSetComponents({ id: components[mcIndex].id, component: mc }, mcIndex);
  };

  useEffect(() => {
    const updatedComponent = { id, component: data };
    handlerSetComponents(updatedComponent, componentIndex);
    syncDesktopSubmenu();
  }, [data]);

  const items = data.items || [];
  const secondaryLinks = data.secondaryLinks || [];
  const itemIndex = items.length === 0 ? -1 : Math.min(selItem, items.length - 1);
  const item = itemIndex === -1 ? null : items[itemIndex];
  const cities = (item && item.cities) || [];
  const cityIndex = cities.length === 0 ? -1 : Math.min(selCity, cities.length - 1);
  const city = cityIndex === -1 ? null : cities[cityIndex];

  const setItems = next => setValue(dispatch)('items', next);
  const patchItem = next => setItems(updateAt(items, itemIndex, next));
  const patchCity = next => patchItem({ ...item, cities: updateAt(cities, cityIndex, next) });

  const handleDragEnd = (result) => {
    const next = applyDrag({ items, secondaryLinks }, result);
    if (next.items === items && next.secondaryLinks === secondaryLinks) return;
    if (next.items !== items) setValue(dispatch)('items', next.items);
    if (next.secondaryLinks !== secondaryLinks) setValue(dispatch)('secondaryLinks', next.secondaryLinks);
  };

  return (
    <div className="flex-1 mr-4 mnv">
      <DragDropContext onDragEnd={handleDragEnd}>
        <NavItemsBar
          items={items}
          selected={itemIndex}
          onSelect={(i) => { setSelItem(i); setSelCity(0); }}
          onChange={setItems}
        />

        {item && item.hasDropdown && (
          <div className="mnv-canvas">
            <NavCityTabs
              itemIndex={itemIndex}
              cities={cities}
              selected={cityIndex}
              onSelect={setSelCity}
              onChange={next => patchItem({ ...item, cities: next })}
            />
            {city && (
              <div>
                <NavCityHeader
                  city={city}
                  onChange={patchCity}
                  onDelete={() => {
                    setSelCity(0);
                    patchItem({ ...item, cities: removeAt(cities, cityIndex) });
                  }}
                />
                <NavColumnsBoard
                  itemIndex={itemIndex}
                  cityIndex={cityIndex}
                  columns={city.columns || []}
                  onChange={columns => patchCity({ ...city, columns })}
                />
              </div>
            )}
          </div>
        )}
        {item && !item.hasDropdown && (
          <p className="mnv-note">
            {`"${(item.text && item.text.en) || item.key || 'This item'}" is a plain link — use ✎ on its pill to edit text and URL, or tick "has dropdown" to give it cities.`}
          </p>
        )}

        <div className="mnv-secondary">
          <span className="mnv-lab">Secondary links (mobile drawer bottom) — rows can be dragged here from any column</span>
          <NavLinksArea
            droppableId="secondary"
            links={secondaryLinks}
            addLabel="secondary link"
            onChange={links => setValue(dispatch)('secondaryLinks', links)}
          />
        </div>
      </DragDropContext>
    </div>
  );
};

MainNavMenu.propTypes = {
  components: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  handlerSetComponents: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
};

export default MainNavMenu;
